<script>
import Joi from '@hapi/joi'
import numeral from 'numeral'

import utilStyles from '@/assets/scss/modules/utilities.module.scss'

// Components
import {
  ActionBar,
  //ActionBarButton,
  ActionBarTitle
} from '@/components/ui/action-bar'
import { PageContainer } from '@/components/page/page-container'
import { AppContainer } from '@/components/ui/container'
import { ClientProfileDisplay } from '@/components/page/client-profile'
import {
  PaymentSummary,
  PaymentSummaryItem
} from '@/components/page/payment-summary'
import {
  PaymentMethod,
  PaymentMethodItem
} from '@/components/page/payment-method'
import { PaymentEvidence } from '@/components/page/payment-evidence'
import { showPrintDialog } from '@/components/dialog/receipt'

import Layout from './Layout'
import Info from './Info'
import PaymentOverview from './PaymentOverview'
import InvoiceCheckbox from './InvoiceCheckbox'

const RedemptionDetail = () => import('./RedemptionDetail')
const ExtensionDetail = () => import('./ExtensionDetail')

// Functions
import { back } from '@/utils/navigation'
import { mapActions, mapState } from 'vuex'
import { usePaymentMethod, usePaymentMethodMethod } from '@/use/payment'
import { PawnHistoryService } from '@/services'
import { useLoading } from '@/use/loading'

export default {
  data() {
    return {
      discount: 0,
      invoice: false,
      methods: [
        usePaymentMethod({
          method: usePaymentMethodMethod({
            id: 1,
            name: 'เงินสด',
            isFeeFree: true
          })
        })
      ],
      evidences: []
    }
  },

  computed: {
    ...mapState('pawnDetail', [
      'detail',
      'redemptionForm',
      'extensionForm',
      'paymentForm'
    ]),

    title() {
      const { type } = this.$route.params

      switch (type) {
        case 'redemption':
          return 'ไถ่ถอน'

        case 'extension':
          return 'ต่อขายฝาก'

        default:
          return null
      }
    },

    amount() {
      const { type } = this.$route.params

      switch (type) {
        case 'redemption':
          return this.redemptionForm
            ? this.redemptionForm.amount + this.redemptionForm.redemptionAmount
            : 0

        case 'extension':
          return this.extensionForm ? this.extensionForm.amount : 0

        default:
          return 0
      }
    },

    type() {
      return this.$route.params.type
    },

    totalPaidAmount() {
      return this.methods.reduce((prev, curr) => prev + curr.amount, 0)
    },

    totalFeeAmount() {
      return this.methods.reduce(
        (prev, curr) => prev + (curr.amount * curr.fee) / 100,
        0
      )
    },

    totalWithFee() {
      return this.amount - this.discount + this.totalFeeAmount
    },

    receivedAmount() {
      return this.totalPaidAmount + this.totalFeeAmount
    },

    change() {
      return this.receivedAmount - this.totalWithFee
    }
  },

  methods: {
    ...mapActions('pawnDetail', ['reset']),

    back() {
      this.reset()

      back({ name: 'PawnDetail' })
    },

    async confirm() {
      const validate = () => {
        const schema = Joi.object({
          discount: Joi.number(),
          methods: Joi.array()
            .items(
              Joi.object({
                method: Joi.object(),
                amount: Joi.number()
              })
            )
            .min(1)
        })

        return schema.validateAsync(this.$data, { allowUnknown: true })
      }

      const extend = () => {
        const data = {
          amount: this.extensionForm.amount,
          action_datetime: this.extensionForm.date,
          new_expire_datetime: this.extensionForm.dueDate,
          receipt: {
            datetime: this.extensionForm.date,
            note: '',
            attachments: this.evidences.map(evidence => evidence.id),
            discount: this.discount,
            is_vat: this.invoice,
            payments: this.methods.map(method => ({
              payment_method: method.method.id,
              ref: method.ref,
              amount: method.amount,
              note: '',
              ...(!method.method.isFeeFree && { fee_percentage: +method.fee })
            }))
          }
        }

        return PawnHistoryService.extend(this.$route.params.id, data)
      }

      const redeem = () => {
        const data = {
          amount: this.redemptionForm.amount,
          action_datetime: this.redemptionForm.date,
          principal_return_amount: this.redemptionForm.redemptionAmount,
          receipt: {
            datetime: this.redemptionForm.date,
            note: '',
            attachments: this.evidences.map(evidence => evidence.id),
            discount: this.discount,
            is_vat: this.invoice,
            payments: this.methods.map(method => ({
              payment_method: method.method.id,
              ref: method.ref,
              amount: method.amount,
              note: '',
              ...(!method.method.isFeeFree && { fee_percentage: +method.fee })
            }))
          }
        }

        return PawnHistoryService.redeem(this.$route.params.id, data)
      }

      const showPrint = async (activityId, change, type) => {
        const action = await showPrintDialog({
          change: change
        })

        switch (action) {
          case 'slip':
            return window.open(
              type === 'extension'
                ? this.$router.resolve({
                    name: 'PawnExtendPrint',
                    params: {
                      id: this.$route.params.id,
                      activityId: activityId
                    }
                  }).href
                : this.$router.resolve({
                    name: 'PawnRedeemPrint',
                    params: {
                      id: this.$route.params.id,
                      activityId: activityId
                    }
                  }).href
            )
        }
      }

      try {
        await validate()

        const loading = useLoading()

        let result = undefined

        try {
          switch (this.type) {
            case 'extension':
              result = await extend()
              await showPrint(result.data.result.id, this.change, this.type)

              this.$notify.success('ต่อขายฝากเรียบร้อยแล้ว')

              break

            case 'redemption':
              result = await redeem()
              await showPrint(result.data.result.id, this.change, this.type)

              this.$notify.success('ไถ่ถอนเรียบร้อยแล้ว')

              break
          }

          this.reset()

          back({ name: 'PawnDetail' })
        } catch (e) {
          this.$notify.error(e.message)
        } finally {
          loading.close()
        }
      } catch (e) {
        this.$message.warning('กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง')
      }
    },

    addMethod() {
      this.methods = [...this.methods, usePaymentMethod()]
    },

    removeMethod(index) {
      this.methods = this.methods.filter(
        (item, itemIndex) => itemIndex !== index
      )
    },

    handleInvoiceCheckboxChange(value) {
      this.invoice = value
    },

    handlePaymentMethodAdd() {
      this.addMethod()
    },

    handlePaymentEvidenceChange(value) {
      this.evidences = value
    }
  },

  render() {
    return (
      <div>
        <ActionBar>
          <el-button slot="action" onClick={this.back}>
            กลับ
          </el-button>
          <el-button slot="action" type="primary" onClick={this.confirm}>
            ยืนยัน
          </el-button>
          <ActionBarTitle>{this.title}</ActionBarTitle>
        </ActionBar>
        <PageContainer>
          <AppContainer>
            <Layout>
              <el-card header="รายการ">
                <ClientProfileDisplay
                  avatar={this.detail.customer.avatar}
                  name={this.detail.customer.fullName}
                  tel={this.detail.customer.tel}
                />

                <Info
                  ticketNo={this.detail.ticketNo}
                  date={this.detail.date}
                  totalWeight={this.detail.weight}
                  amount={this.detail.amount}
                  interestPerMonth={this.detail.interestPerMonth}
                  interestRate={this.detail.interestRate}
                  style={{ marginTop: '1.5rem' }}
                />

                {this.type === 'redemption' && this.redemptionForm && (
                  <RedemptionDetail
                    date={this.redemptionForm.date}
                    amount={this.redemptionForm.amount}
                    redemptionAmount={this.redemptionForm.redemptionAmount}
                    style={{ marginTop: '1.5rem' }}
                  />
                )}

                {this.type === 'extension' && this.extensionForm && (
                  <ExtensionDetail
                    date={this.extensionForm.date}
                    dueDate={this.extensionForm.dueDate}
                    amount={this.extensionForm.amount}
                    style={{ marginTop: '1.5rem' }}
                  />
                )}
              </el-card>
              <el-card header="ยอดเงินที่รับชำระ">
                <PaymentOverview
                  amount={this.amount}
                  discount={this.discount}
                  fee={this.totalFeeAmount}
                  on={{
                    'update:discount': value => {
                      this.discount = value
                    }
                  }}
                />

                <PaymentSummary style={{ marginTop: '1.5rem' }}>
                  <PaymentSummaryItem title="ได้รับแล้ว">
                    ฿ {numeral(this.receivedAmount).format('0,0.00')}
                  </PaymentSummaryItem>
                  <PaymentSummaryItem title="เงินทอน / เงินคืน">
                    <span class={utilStyles.colorPrimary}>
                      ฿ {numeral(this.change).format('0,0.00')}
                    </span>
                  </PaymentSummaryItem>
                </PaymentSummary>

                <InvoiceCheckbox
                  value={this.invoice}
                  onChange={this.handleInvoiceCheckboxChange}
                  style={{ marginTop: '1.5rem' }}
                />

                <PaymentMethod
                  title="ช่องทางที่รับชำระ"
                  style={{ marginTop: '1.5rem' }}
                  onAdd={this.handlePaymentMethodAdd}
                >
                  {this.methods.map((item, index) => (
                    <PaymentMethodItem
                      key={index}
                      index={index}
                      method={item.method}
                      refCode={item.ref}
                      amount={item.amount}
                      fee={item.fee}
                      on={{
                        'update:method': value => {
                          item.method = value
                        },
                        'update:refCode': value => {
                          item.ref = value
                        },
                        'update:amount': value => {
                          item.amount = value
                        },
                        'update:fee': value => {
                          item.fee = value
                        },
                        remove: () => {
                          this.removeMethod(index)
                        }
                      }}
                    />
                  ))}
                </PaymentMethod>

                <PaymentEvidence
                  value={this.evidences}
                  onChange={this.handlePaymentEvidenceChange}
                  style={{ marginTop: '1.5rem' }}
                />
              </el-card>
            </Layout>
          </AppContainer>
        </PageContainer>
      </div>
    )
  }
}
</script>
